import type { VideoAssetStatus } from './videoAsset.types';
import { isReadyStatus, isReadyVideo } from './isReadyVideo';

/**
 * Shape of a deleted-or-blanked media asset. Photo and VideoAsset both carry `url` + `deleted`,
 * so the same two signals decide tombstoning for a photo.
 */
type TombstonableAsset = { url?: string; deleted?: boolean } | null | undefined;

type TombstonableVideoAsset = Readonly<{ url?: string; deleted?: boolean; status?: VideoAssetStatus | null }> | null | undefined;

export const isPhotoTombstoned = (photo: TombstonableAsset): boolean => {
  if (!photo) return false;
  if (photo.deleted === true) return true;
  if (photo.url === '') return true;
  return false;
};

/**
 * A video's own rule. `deleted` always counts. A blank `url` counts only on a READY asset, or one whose status
 * was not selected: photo_service blanks `url` on every PROCESSING and FAILED video, and admin must
 * still see those tiles.
 */
export const isVideoTombstoned = (videoAsset: TombstonableVideoAsset): boolean => {
  if (!videoAsset) return false;
  if (videoAsset.deleted === true) return true;
  return videoAsset.url === '' && (!videoAsset.status || isReadyStatus(videoAsset.status));
};

type MediaPhotoMember = { __typename?: 'MediaPhoto' | 'MediaVideo'; photo?: TombstonableAsset };
type MediaVideoMember = { __typename?: 'MediaPhoto' | 'MediaVideo'; videoAsset?: TombstonableVideoAsset };

/**
 * Deliberately photo-only: it is the filter that keeps a video off a surface that can only show
 * photos — cover-image pickers, colour palettes, header photos. Most `EventMedia` consumers want
 * exactly this, so it stays narrow. Use `isActiveMedia` on a surface that renders both kinds.
 */
export const isActiveMediaPhoto = <T extends MediaPhotoMember>(m: T): m is Extract<T, { __typename?: 'MediaPhoto' }> =>
  m.__typename === 'MediaPhoto' && !isPhotoTombstoned(m.photo);

/**
 * A video with no `videoAsset` is inactive: the asset is the only thing there is to play, and the
 * field is nullable because it resolves across services. A video that is not READY is still active here —
 * whether it may be played or shown to a guest is `isReadyVideo`'s call, not this one.
 */
export const isActiveMediaVideo = <T extends MediaVideoMember>(m: T): m is Extract<T, { __typename?: 'MediaVideo' }> =>
  m.__typename === 'MediaVideo' && !!m.videoAsset && !isVideoTombstoned(m.videoAsset);

type ReadyMediaVideo<T extends MediaVideoMember> = Extract<T, { __typename?: 'MediaVideo' }> & {
  videoAsset: NonNullable<Extract<T, { __typename?: 'MediaVideo' }>['videoAsset']>;
};

/** A video a guest may see: active and READY. Also narrows `videoAsset` to non-null, which `isActiveMediaVideo` cannot. */
export const isReadyActiveMediaVideo = <T extends MediaVideoMember>(m: T): m is ReadyMediaVideo<T> => isActiveMediaVideo(m) && isReadyVideo(m.videoAsset);

/** Keeps both arms of the `EventMedia` union — for a surface that renders photos and videos together. */
export const isActiveMedia = <T extends MediaPhotoMember | MediaVideoMember>(m: T): m is Extract<T, { __typename?: 'MediaPhoto' } | { __typename?: 'MediaVideo' }> =>
  isActiveMediaPhoto(m) || isActiveMediaVideo(m);
